import type { ComponentBaseProps } from "@/ui/componentBase";
import type React from "react";
import type {
  ComponentBaseContext,
  ComponentBaseMapper,
  ExecutionDetailsContext,
  NodeInfo,
  OutputPayload,
  SubtitleContext,
} from "../types";
import type { MetadataItem } from "@/ui/metadataList";
import { renderTimeAgo } from "@/components/TimeAgo";
import { formatTimestamp, stringOrDash } from "./common";
import { baseProps } from "./base";

interface GetServiceConfiguration {
  service?: string;
}

interface GetServiceOutput {
  serviceId?: string;
  serviceName?: string;
  type?: string;
  suspended?: string;
  dashboardUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  customDomains?: Array<{
    id?: string;
    name?: string;
    verificationStatus?: string;
  }>;
}

function metadataList(node: NodeInfo): MetadataItem[] {
  const metadata: MetadataItem[] = [];
  const configuration = node.configuration as GetServiceConfiguration | undefined;

  if (configuration?.service) {
    metadata.push({ icon: "server", label: `Service: ${configuration.service}` });
  }

  return metadata;
}

export const getServiceMapper: ComponentBaseMapper = {
  props(context: ComponentBaseContext): ComponentBaseProps {
    const base = baseProps(context.nodes, context.node, context.componentDefinition, context.lastExecutions);
    return { ...base, metadata: metadataList(context.node) };
  },

  subtitle(context: SubtitleContext): string | React.ReactNode {
    const timestamp = context.execution.updatedAt || context.execution.createdAt;
    return timestamp ? renderTimeAgo(new Date(timestamp)) : "";
  },

  getExecutionDetails(context: ExecutionDetailsContext): Record<string, string> {
    const outputs = context.execution.outputs as { default?: OutputPayload[] } | undefined;
    const result = outputs?.default?.[0]?.data as GetServiceOutput | undefined;

    return {
      "Retrieved At": context.execution.createdAt ? new Date(context.execution.createdAt).toLocaleString() : "-",
      "Service ID": stringOrDash(result?.serviceId),
      "Service Name": stringOrDash(result?.serviceName),
      Type: stringOrDash(result?.type),
      Suspended: result?.suspended === undefined ? "-" : result.suspended === "suspended" ? "Yes" : "No",
      "Dashboard URL": stringOrDash(result?.dashboardUrl),
      "Custom Domains": formatCustomDomains(result?.customDomains),
      "Created At": formatTimestamp(result?.createdAt),
      "Updated At": formatTimestamp(result?.updatedAt),
    };
  },
};

function formatCustomDomains(domains?: GetServiceOutput["customDomains"]): string {
  if (!domains?.length) {
    return "-";
  }

  return domains
    .map((domain) => {
      const status = domain.verificationStatus ? ` (${domain.verificationStatus})` : "";
      return `${domain.name || domain.id || "-"}${status}`;
    })
    .join(", ");
}
